import { z } from 'zod';
import { eventSchema } from './envelope';

export const DIRECTORY_PERSON_CREATED_V1 = 'directory.person.created.v1' as const;
export const DIRECTORY_PERSON_SELF_REGISTERED_V1 = 'directory.person.self_registered.v1' as const;
export const DIRECTORY_PERSON_UPDATED_V1 = 'directory.person.updated.v1' as const;
export const DIRECTORY_PERSON_DEACTIVATED_V1 = 'directory.person.deactivated.v1' as const;
export const DIRECTORY_PERSON_DEACTIVATION_SCHEDULED_V1 =
  'directory.person.deactivation.scheduled.v1' as const;
export const DIRECTORY_PERSON_REACTIVATED_V1 = 'directory.person.reactivated.v1' as const;
export const DIRECTORY_GROUP_CREATED_V1 = 'directory.group.created.v1' as const;
export const DIRECTORY_GROUP_DELETED_V1 = 'directory.group.deleted.v1' as const;
export const DIRECTORY_GROUP_MEMBERSHIP_CHANGED_V1 =
  'directory.group.membership.changed.v1' as const;
export const DIRECTORY_PERSON_SITE_CHANGED_V1 = 'directory.person.site.changed.v1' as const;

export const personCreatedV1Schema = eventSchema({
  personId: z.guid(),
  employeeNumber: z.string().nullable(),
  email: z.string().email().nullable(),
  /** Stap 31: een tijdelijke krijgt de rol Tijdelijk in plaats van de standaardrol. */
  personType: z.enum(['employee', 'temporary', 'contractor']).optional(),
  /**
   * Hoe de persoon erbij kwam (LOG-1). `manual` = aangemaakt in het beheer; alleen die krijgt
   * meteen een uitnodigingsmail, zodat een import niet ongevraagd honderden mails verstuurt.
   */
  origin: z.enum(['manual', 'self_registration', 'import']).optional(),
});

/**
 * Een medewerker registreerde zich zelf met een uitnodiging (Stap 34). Naast `person.created`,
 * niet in plaats ervan: wie op een nieuwe medewerker reageert, hoeft niet te weten hoe hij er kwam.
 */
export const personSelfRegisteredV1Schema = eventSchema({
  personId: z.guid(),
  invitationId: z.guid(),
  merged: z.boolean(),
});
export type PersonSelfRegisteredV1 = z.infer<typeof personSelfRegisteredV1Schema>;

/** `changed` noemt alleen de velden die echt anders zijn; leeg betekent: geen event. */
export const personUpdatedV1Schema = eventSchema({
  personId: z.guid(),
  changed: z.array(z.string()),
});

export const personDeactivatedV1Schema = eventSchema({
  personId: z.guid(),
  /** `erased`: het account werd na een verwijderverzoek gewist (DEL-1, ADR-0098). */
  reason: z.enum(['manual', 'planned_exit', 'import', 'erased']),
});

/**
 * Uit dienst op een datum. De reden staat er bewust niet in: die is vrije tekst van HR en
 * hoort niet in een event dat straks door het hele platform reist.
 */
export const personDeactivationScheduledV1Schema = eventSchema({
  personId: z.guid(),
  plannedExitDate: z.string().date(),
});

export const personReactivatedV1Schema = eventSchema({
  personId: z.guid(),
});

export const groupCreatedV1Schema = eventSchema({
  groupId: z.guid(),
  name: z.string(),
  kind: z.string(),
});

export const groupDeletedV1Schema = eventSchema({
  groupId: z.guid(),
  name: z.string(),
});

/**
 * Eén event per wijziging, met alle toegevoegde en verwijderde personen erin. Tien
 * mensen tegelijk aan een ploeg toevoegen is één gebeurtenis, geen tien.
 */
export const groupMembershipChangedV1Schema = eventSchema({
  groupId: z.guid(),
  added: z.array(z.guid()),
  removed: z.array(z.guid()),
});

export type PersonCreatedV1 = z.infer<typeof personCreatedV1Schema>;
export type PersonUpdatedV1 = z.infer<typeof personUpdatedV1Schema>;
export type PersonDeactivatedV1 = z.infer<typeof personDeactivatedV1Schema>;
export type PersonDeactivationScheduledV1 = z.infer<typeof personDeactivationScheduledV1Schema>;
export type PersonReactivatedV1 = z.infer<typeof personReactivatedV1Schema>;
export type GroupCreatedV1 = z.infer<typeof groupCreatedV1Schema>;
export type GroupDeletedV1 = z.infer<typeof groupDeletedV1Schema>;
export type GroupMembershipChangedV1 = z.infer<typeof groupMembershipChangedV1Schema>;

/**
 * De vestiging van een medewerker is gewijzigd (Stap 29).
 *
 * `from` en `to` staan er beide in, want wie hierop reageert, moet van twee sites iets weten: de
 * oude ploegenlijst van Gent wordt korter en die van Kortrijk langer. Een event met alleen de
 * nieuwe waarde zou de ontvanger dwingen zelf bij te houden wat er stond.
 */
export const personSiteChangedV1Schema = eventSchema({
  personId: z.guid(),
  from: z.guid().nullable(),
  to: z.guid().nullable(),
});

export const directoryEventCatalog = {
  [DIRECTORY_PERSON_SELF_REGISTERED_V1]: personSelfRegisteredV1Schema,
  [DIRECTORY_PERSON_SITE_CHANGED_V1]: personSiteChangedV1Schema,
  [DIRECTORY_PERSON_CREATED_V1]: personCreatedV1Schema,
  [DIRECTORY_PERSON_UPDATED_V1]: personUpdatedV1Schema,
  [DIRECTORY_PERSON_DEACTIVATED_V1]: personDeactivatedV1Schema,
  [DIRECTORY_PERSON_DEACTIVATION_SCHEDULED_V1]: personDeactivationScheduledV1Schema,
  [DIRECTORY_PERSON_REACTIVATED_V1]: personReactivatedV1Schema,
  [DIRECTORY_GROUP_CREATED_V1]: groupCreatedV1Schema,
  [DIRECTORY_GROUP_DELETED_V1]: groupDeletedV1Schema,
  [DIRECTORY_GROUP_MEMBERSHIP_CHANGED_V1]: groupMembershipChangedV1Schema,
} as const;
